import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ExternalLink, Filter, Inbox, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge, EmptyState, ErrorState, IconButton, LoadingState, useConfirm } from '@/components/primitives';
import { adminApi, formatUtc, IOC_TYPES, plural, toastError, useAdminQuery, useBusy } from './adminApi';
import { AdminSection, Field, FilterChips, IocTypeBadge, IocValue, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 50;

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Pick the campaign that receives approved IOCs. Campaigns are grouped by
 * state so it is clear when the target is already live; either way the IOCs
 * arrive as pending and are only published from the campaign.
 */
function ApproveDialog({ iocs, campaigns, campaignsError, onRetryCampaigns, defaultCampaignId, onOpenChange, onApproved }) {
  const [campaignId, setCampaignId] = useState('');
  const { run, busy } = useBusy();
  const open = Boolean(iocs);
  useEffect(() => {
    if (open) setCampaignId(campaigns.some((c) => c.id === defaultCampaignId) ? defaultCampaignId : '');
  }, [open, defaultCampaignId, campaigns]);

  const drafts = campaigns.filter((c) => c.status === 'pending');
  const live = campaigns.filter((c) => c.status === 'approved');
  const target = campaigns.find((c) => c.id === campaignId);

  const submit = (e) => {
    e.preventDefault();
    if (!campaignId) {
      toast.error('Choose a campaign');
      return;
    }
    run('approve', async () => {
      try {
        if (iocs.length === 1) {
          await adminApi.post(`/admin/auto-iocs/${iocs[0].id}/approve`, { campaign_id: campaignId });
        } else {
          await adminApi.post('/admin/auto-iocs/bulk-approve', { ioc_ids: iocs.map((i) => i.id), campaign_id: campaignId });
        }
        toast.success(`${plural(iocs.length, 'IOC')} moved to ${target?.name || 'the campaign'}`, {
          description: target?.status === 'approved' ? 'They are pending: publish them from the campaign to make them visible.' : 'They are published together with the campaign.',
        });
        onApproved(campaignId);
        onOpenChange(false);
      } catch (err) {
        toastError(err, 'Could not approve the IOCs');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy('approve') && onOpenChange(v)}>
      <DialogContent className="adm-dialog">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>Approve {plural(iocs?.length || 0, 'IOC')} into a campaign</DialogTitle>
            <DialogDescription>
              Approved IOCs leave the review queue and are added to the campaign as pending. Customers only see them once they are published.
            </DialogDescription>
          </DialogHeader>
          {iocs?.length === 1 && <code className="adm-ioc-value adm-ioc-value--block">{iocs[0].value}</code>}
          {campaignsError ? (
            <ErrorState variant="inline" title="Couldn't load campaigns" error={campaignsError} onRetry={onRetryCampaigns} />
          ) : (
            <Field label="Campaign" hint={!campaigns.length ? 'No draft or live campaigns exist. Create one on the Hunt campaigns tab first.' : undefined}>
              {(id, hint) => (
                <select id={id} aria-describedby={hint} className="adm-input adm-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} data-testid="approve-campaign-select">
                  <option value="">Choose a campaign…</option>
                  {drafts.length > 0 && (
                    <optgroup label="Drafts (pending review)">
                      {drafts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  )}
                  {live.length > 0 && (
                    <optgroup label="Live campaigns">
                      {live.map((c) => <option key={c.id} value={c.id}>{c.name} (live)</option>)}
                    </optgroup>
                  )}
                </select>
              )}
            </Field>
          )}
          {target?.status === 'approved' && (
            <p className="adm-note adm-note--warning" role="note">
              This campaign is live. The IOCs arrive as pending; publish them from the campaign after review.
            </p>
          )}
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy('approve')}>Cancel</Button>
            <Button type="submit" disabled={busy('approve') || !campaignId} data-testid="approve-confirm-btn">
              {busy('approve') ? <><Loader2 className="is-spinner" aria-hidden="true" />Approving…</> : <><Check aria-hidden="true" />Approve {plural(iocs?.length || 0, 'IOC')}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The auto-collected IOC review queue: server-paged table with bulk actions. */
export default function AutoIocsTab({ onChanged }) {
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState(null);
  const [q, setQ] = useState('');
  const [source, setSource] = useState(null); // { id, name }
  const [offset, setOffset] = useState(0);
  const query = useDebounced(q.trim());

  useEffect(() => { setOffset(0); }, [status, type, query, source]);

  const { data, error, loading, reload, setData } = useAdminQuery(
    () => adminApi.get('/admin/auto-iocs/page', {
      params: { status, type: type || undefined, q: query || undefined, source_attack_id: source?.id, limit: PAGE, offset },
    }).then((r) => r.data),
    [status, type, query, source?.id, offset]
  );
  const campaignsQuery = useAdminQuery(
    () => adminApi.get('/admin/threat-hunt-campaigns').then((r) => (r.data.campaigns || []).filter((c) => c.status !== 'rejected')),
    []
  );

  const rows = data?.iocs || [];
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [status, type, query, source?.id, offset]);
  const [approving, setApproving] = useState(null);
  const [lastCampaign, setLastCampaign] = useState('');
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();

  const pending = status === 'pending';
  const pageIds = rows.map((r) => r.id);
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPage = selectedRows.length > 0 && !allOnPage;
  const headerBox = useRef(null);
  useEffect(() => { if (headerBox.current) headerBox.current.indeterminate = someOnPage; }, [someOnPage]);

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(pageIds));

  const afterReview = (ids) => {
    // Drop reviewed rows right away; the reload brings in the next ones and fresh counts.
    setData((d) => (d ? { ...d, iocs: d.iocs.filter((i) => !ids.includes(i.id)) } : d));
    setSelected(new Set());
    reload();
    onChanged?.();
  };

  const reject = async (list) => {
    if (list.length > 1) {
      const ok = await confirm({
        title: `Reject ${plural(list.length, 'IOC')}?`,
        description: 'They leave the review queue and are not added to any campaign.',
        confirmLabel: `Reject ${plural(list.length, 'IOC')}`,
        destructive: true,
      });
      if (!ok) return;
    }
    const key = list.length === 1 ? `reject-${list[0].id}` : 'bulk-reject';
    run(key, async () => {
      try {
        if (list.length === 1) await adminApi.post(`/admin/auto-iocs/${list[0].id}/reject`);
        else await adminApi.post('/admin/auto-iocs/bulk-reject', { ioc_ids: list.map((i) => i.id) });
        toast.success(`${plural(list.length, 'IOC')} rejected`);
        afterReview(list.map((i) => i.id));
      } catch (err) {
        toastError(err, 'Could not reject');
      }
    });
  };

  const stats = data?.stats || {};
  const types = data?.types || {};
  const statusOptions = [
    { value: 'pending', label: 'Pending', count: stats.pending },
    { value: 'approved', label: 'Approved', count: stats.approved },
    { value: 'rejected', label: 'Rejected', count: stats.rejected },
  ];
  const typeOptions = useMemo(() => [
    { value: null, label: 'All types', count: Object.values(types).reduce((a, b) => a + b, 0) },
    ...IOC_TYPES.filter((t) => types[t.value]).map((t) => ({ value: t.value, label: t.label, count: types[t.value] })),
  ], [types]);

  return (
    <AdminSection
      title="IOC review queue"
      count={stats.pending}
      description="Indicators extracted automatically from articles. Check each value against its source before approving it into a campaign."
    >
      <Toolbar>
        <FilterChips label="Review status" options={statusOptions} value={status} onChange={setStatus} testIdPrefix="filter" />
      </Toolbar>
      <Toolbar>
        <SearchField value={q} onChange={setQ} label="Search IOCs" placeholder="Search value or source story" testId="auto-ioc-search" />
        <FilterChips label="IOC type" options={typeOptions} value={type} onChange={setType} testIdPrefix="type" />
        {source && (
          <Badge plain icon={Filter} className="adm-filter-pill">
            Story: {source.name}
            <button type="button" className="adm-pill-clear" onClick={() => setSource(null)} aria-label="Clear story filter">
              <X aria-hidden="true" />
            </button>
          </Badge>
        )}
      </Toolbar>

      {pending && selectedRows.length > 0 && (
        <div className="adm-bulkbar" role="region" aria-label="Bulk actions">
          <span className="adm-strong">{plural(selectedRows.length, 'IOC')} selected</span>
          <Button size="sm" onClick={() => setApproving(selectedRows)} data-testid="bulk-approve-btn">
            <Check aria-hidden="true" /> Approve into campaign…
          </Button>
          <Button size="sm" variant="outline" onClick={() => reject(selectedRows)} disabled={busy('bulk-reject')} data-testid="bulk-reject-btn">
            {busy('bulk-reject') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <X aria-hidden="true" />}
            {busy('bulk-reject') ? 'Rejecting…' : 'Reject'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear selection</Button>
        </div>
      )}

      {loading ? (
        <LoadingState label="IOCs" rows={6} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load the review queue" error={error} onRetry={reload} />
      ) : !rows.length ? (
        query || type || source ? (
          <EmptyState variant="compact" title="No matching IOCs" description="Change the search, type or story filter." />
        ) : (
          <EmptyState icon={Inbox} title={pending ? 'The review queue is empty' : `No ${status} IOCs`} description={pending ? 'New indicators appear here as articles are processed.' : undefined} />
        )
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="the queue" />
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="auto-iocs-table">
              <thead>
                <tr>
                  {pending && (
                    <th scope="col" className="adm-col-check">
                      <input
                        ref={headerBox}
                        type="checkbox"
                        className="adm-checkbox"
                        checked={allOnPage}
                        onChange={toggleAll}
                        aria-label={`Select all ${rows.length} IOCs on this page`}
                      />
                    </th>
                  )}
                  <th scope="col" className="adm-col-type">Type</th>
                  <th scope="col" className="adm-col-value">Value</th>
                  <th scope="col">Source story</th>
                  <th scope="col">Collected</th>
                  <th scope="col" className="adm-col-actions">{pending ? <span className="sr-only">Actions</span> : 'Result'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ioc) => (
                  <tr key={ioc.id} aria-selected={pending ? selected.has(ioc.id) : undefined} data-testid={`auto-ioc-${ioc.id}`}>
                    {pending && (
                      <td className="adm-col-check">
                        <input
                          type="checkbox"
                          className="adm-checkbox"
                          checked={selected.has(ioc.id)}
                          onChange={() => toggle(ioc.id)}
                          aria-label={`Select ${ioc.value}`}
                        />
                      </td>
                    )}
                    <td data-label="Type"><IocTypeBadge type={ioc.type} /></td>
                    <td data-label="Value" className="adm-col-value"><IocValue value={ioc.value} type={ioc.type} /></td>
                    <td data-label="Source story" className="adm-source-cell">
                      <span className="adm-break">{ioc.source_attack_name || 'Unknown story'}</span>
                      <span className="adm-inline-actions">
                        {ioc.source_url && (
                          <a href={ioc.source_url} target="_blank" rel="noopener noreferrer" className="adm-link">
                            <ExternalLink aria-hidden="true" /> Article<span className="sr-only"> for {ioc.source_attack_name} (opens in a new tab)</span>
                          </a>
                        )}
                        {ioc.source_attack_id && source?.id !== ioc.source_attack_id && (
                          <button type="button" className="adm-link-btn" onClick={() => setSource({ id: ioc.source_attack_id, name: ioc.source_attack_name || 'story' })}>
                            <Filter aria-hidden="true" /> Only this story
                          </button>
                        )}
                      </span>
                    </td>
                    <td data-label="Collected" className="adm-mono adm-nowrap adm-secondary">{formatUtc(ioc.created_at)}</td>
                    <td className="adm-actions-cell" data-label={pending ? undefined : 'Result'}>
                      {pending ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setApproving([ioc])} data-testid={`approve-ioc-${ioc.id}`}>
                            <Check aria-hidden="true" /> Approve
                          </Button>
                          <IconButton
                            label={`Reject ${ioc.value}`}
                            icon={busy(`reject-${ioc.id}`) ? Loader2 : X}
                            variant="danger"
                            disabled={busy(`reject-${ioc.id}`)}
                            className={busy(`reject-${ioc.id}`) ? 'adm-spin' : undefined}
                            onClick={() => reject([ioc])}
                            data-testid={`reject-ioc-${ioc.id}`}
                          />
                        </>
                      ) : (
                        <span className="adm-secondary">
                          {ioc.status === 'approved' ? 'Approved' : 'Rejected'} {ioc.reviewed_at ? formatUtc(ioc.reviewed_at) : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={data.total} onChange={setOffset} label="Review queue pages" />
        </>
      )}

      <ApproveDialog
        iocs={approving}
        campaigns={campaignsQuery.data || []}
        campaignsError={campaignsQuery.error}
        onRetryCampaigns={campaignsQuery.reload}
        defaultCampaignId={lastCampaign}
        onOpenChange={(o) => !o && setApproving(null)}
        onApproved={(cid) => {
          setLastCampaign(cid);
          afterReview((approving || []).map((i) => i.id));
          campaignsQuery.reload();
        }}
      />
      {confirmDialog}
    </AdminSection>
  );
}
